import { NavLink, Outlet } from 'react-router'
import { useState } from 'react'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { useAccount, useChainId, useSwitchChain } from 'wagmi'
import { mainnet } from 'viem/chains'
import { SUPPORTED_CHAIN_IDS } from '../../contracts/addresses'
import { BRAND, KAVACH_X_URL, MANTIS_X_URL } from '../../constants/brandAssets'
import { addTokenToMetaMask } from '../../utils/wallet'
import { getWalletTokenByChain } from '../../constants/tokens'

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/swap', label: 'Swap' },
  { to: '/swap/stable', label: 'Stable' },
  { to: '/liquidity/v3', label: 'CLMM Liq' },
  { to: '/liquidity/cpmm', label: 'CPMM Liq' },
  { to: '/liquidity/stable', label: 'Stable Liq' },
  { to: '/pools', label: 'Pools' },
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/analytics', label: 'Data' },
  { to: '/settings', label: 'Settings' },
] as const

/** Nav pill size (~10% smaller than original default) — same for all primary nav links */
const NAV_PILL =
  'rounded-xl px-[0.68rem] py-[0.56rem] text-[0.8rem] font-medium transition-colors md:px-[0.9rem] md:py-[0.62rem] md:text-[0.9rem]'

function navLinkClass(isActive: boolean) {
  return [
    NAV_PILL,
    isActive
      ? 'bg-emerald-500/12 text-emerald-400 shadow-[0_0_0_1px_rgba(16,185,129,0.35)]'
      : 'text-slate-400 hover:bg-white/[0.04] hover:text-white',
  ].join(' ')
}

export function AppShell() {
  const chainId = useChainId()
  const { isConnected } = useAccount()
  const { switchChain, isPending } = useSwitchChain()
  const [walletMessage, setWalletMessage] = useState<string | null>(null)
  const [isAddingToken, setIsAddingToken] = useState(false)

  const supported = SUPPORTED_CHAIN_IDS.includes(chainId as (typeof SUPPORTED_CHAIN_IDS)[number])
  const walletToken = chainId ? getWalletTokenByChain(chainId) : null

  const handleAddToken = async () => {
    if (!walletToken) return
    setWalletMessage(null)
    setIsAddingToken(true)

    try {
      const wasAdded = await addTokenToMetaMask(walletToken)
      setWalletMessage(wasAdded ? `${walletToken.symbol} was added to MetaMask.` : `MetaMask request was cancelled.`)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to add token to wallet.'
      setWalletMessage(message)
    } finally {
      setIsAddingToken(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-kavach-bg text-slate-100">
      <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-kavach-bg/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-6">
          <NavLink
            to="/"
            className="flex items-center gap-2 font-uchiyama text-lg font-normal tracking-wide text-white md:text-xl"
          >
            <img
              src={BRAND.headerLogo}
              alt="Kavachswap"
              width={36}
              height={36}
              className="h-9 w-9 shrink-0 rounded-lg object-contain ring-1 ring-white/10"
              decoding="async"
            />
            <span className="text-white">KAVACH</span>
            <span className="text-emerald-400">SWAP</span>
          </NavLink>

          <nav
            className="order-3 flex w-full min-w-0 max-w-[min(100vw-2rem,100%)] flex-nowrap gap-1.5 overflow-x-auto overflow-y-hidden overscroll-x-contain py-0.5 [-ms-overflow-style:none] [scrollbar-width:thin] md:order-none md:max-w-none md:flex-wrap md:justify-center md:overflow-visible [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/15"
            aria-label="Primary"
          >
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }: { isActive: boolean }) => navLinkClass(isActive)}
              >
                {item.to === '/settings' ? (
                  <span className="inline-flex items-center gap-2">
                    <img
                      src={BRAND.settingsNavLogo}
                      alt=""
                      width={22}
                      height={22}
                      className="h-[22px] w-[22px] shrink-0 rounded-md object-contain ring-1 ring-white/10"
                      decoding="async"
                    />
                    {item.label}
                  </span>
                ) : (
                  item.label
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="flex items-center gap-2 rounded-xl px-1 py-1 ring-1 ring-white/[0.08]">
              <img
                src={BRAND.twitterNavIcon}
                alt=""
                width={36}
                height={36}
                className="h-9 w-9 shrink-0 rounded-lg object-contain ring-1 ring-white/10"
                decoding="async"
              />
              <div className="flex flex-col gap-0.5 pr-2 text-left sm:flex-row sm:items-center sm:gap-1.5 sm:pr-3">
                <a
                  href={KAVACH_X_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap text-xs font-medium text-slate-400 hover:text-emerald-400 md:text-sm"
                >
                  @kavachswap16098
                </a>
                <span className="hidden text-slate-600 sm:inline" aria-hidden>
                  ·
                </span>
                <a
                  href={MANTIS_X_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap text-xs font-medium text-slate-400 hover:text-emerald-400 md:text-sm"
                >
                  @1mantisshrimp
                </a>
              </div>
            </div>
            <a
              href="https://kavachswap.com/app.html"
              target="_blank"
              rel="noopener noreferrer"
              className={`${NAV_PILL} text-slate-400 ring-1 ring-white/[0.08] hover:bg-white/[0.04] hover:text-emerald-400`}
            >
              Solana DEX
            </a>
            <a
              href="https://data.kavachswap.com/"
              target="_blank"
              rel="noopener noreferrer"
              className={`${NAV_PILL} text-slate-400 ring-1 ring-white/[0.08] hover:bg-white/[0.04] hover:text-emerald-400`}
            >
              L2
            </a>
            <img
              src={BRAND.headerMarkNextToWallet}
              alt=""
              width={36}
              height={36}
              className="h-9 w-9 shrink-0 rounded-lg object-contain opacity-95 ring-1 ring-white/10"
              decoding="async"
            />
            {walletToken && isConnected ? (
              <button
                type="button"
                className="rounded-xl bg-emerald-500/12 px-3 py-2 text-xs font-semibold text-emerald-300 transition hover:bg-emerald-500/15 disabled:opacity-50"
                onClick={handleAddToken}
                disabled={isAddingToken}
              >
                {isAddingToken ? 'Adding…' : `Add ${walletToken.symbol}`}
              </button>
            ) : null}
            <ConnectButton showBalance={false} chainStatus="icon" accountStatus="address" />
          </div>
        </div>

        {isConnected && !supported && (
          <div className="border-t border-amber-500/20 bg-amber-500/10 px-4 py-2 text-center text-sm text-amber-100">
            Unsupported network. Switch to Ethereum.
            <button
              type="button"
              className="ml-3 rounded-md bg-amber-500/20 px-2 py-0.5 font-medium text-amber-50 hover:bg-amber-500/30 disabled:opacity-50"
              disabled={isPending}
              onClick={() => switchChain({ chainId: mainnet.id })}
            >
              Mainnet
            </button>
          </div>
        )}
      {walletMessage ? (
        <div className="border-t border-slate-600/40 bg-slate-950/70 px-4 py-3 text-center text-xs text-slate-300">
          {walletMessage}
        </div>
      ) : null}
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-6 md:px-6">
        <Outlet />
      </main>

      <footer className="border-t border-white/[0.06] px-4 py-6 text-center text-xs text-slate-500">
        <div className="mb-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <NavLink to="/terms" className="hover:text-emerald-400">
            Terms
          </NavLink>
          <span className="opacity-40">·</span>
          <NavLink to="/privacy" className="hover:text-emerald-400">
            Privacy
          </NavLink>
          <span className="opacity-40">·</span>
          <NavLink to="/risk" className="hover:text-emerald-400">
            Risk disclosure
          </NavLink>
          <span className="opacity-40">·</span>
          <NavLink to="/non-custodial" className="hover:text-emerald-400">
            Non-custodial
          </NavLink>
        </div>
        <span className="font-display font-semibold text-slate-400">KAVACHSWAP</span>
        <span className="mx-2 opacity-40">·</span>
        Ethereum · CLMM · Stable · CPMM
        <span className="mx-2 opacity-40">·</span>
        <a className="text-emerald-400/80 hover:text-emerald-400" href="https://etherscan.io" target="_blank" rel="noreferrer">
          Etherscan
        </a>
      </footer>
    </div>
  )
}


